<template>
    <div class="update-location-component">
        <div class="location-controls d-flex align-items-center gap-2 mb-3">
            <button 
                type="button" 
                class="btn btn-primary"
                :class="{ 'btn-secondary': isLocating }"
                :disabled="isLocating"
                @click="updateLocation"
            >
                <i v-if="isLocating" class="fa fa-spinner fa-spin me-1"></i>
                <i v-else-if="locationStatus === 'success'" class="fa fa-map-marker-alt me-1"></i>
                <i v-else-if="locationStatus === 'error'" class="fa fa-exclamation-triangle me-1"></i>
                <i v-else class="fa fa-location-arrow me-1"></i>
                {{ buttonText }}
            </button>

            <button 
                v-if="hasLocation"
                type="button" 
                class="btn btn-outline-secondary btn-sm"
                @click="clearLocation"
                title="Clear saved location"
            >
                <i class="fa fa-times me-1"></i>
                Clear Location
            </button>

            <span v-if="hasLocation" class="text-muted small">
                Location saved {{ locationTimeText }}
            </span>
        </div>

        <div v-if="locationStatus === 'error'" class="alert alert-warning alert-sm">
            <i class="fa fa-exclamation-triangle me-1"></i>
            {{ errorMessage }}
        </div>

        <div v-if="locationStatus === 'success'" class="alert alert-success alert-sm">
            <i class="fa fa-check-circle me-1"></i>
            Location updated successfully!
        </div>
    </div>
</template>

<script>
import { getUserLocation, setUserLocation, clearUserLocation, getRelativeTime } from '@shared/utils'

export default {
    name: 'UpdateLocation',
    emits: ['location-updated', 'location-cleared'],
    data() {
        return {
            isLocating: false,
            locationStatus: null, // null, 'locating', 'success', 'error'
            errorMessage: '',
            locationData: null
        }
    },
    computed: {
        buttonText() {
            if (this.isLocating) {
                return 'Getting Location...';
            }
            if (this.locationStatus === 'success') {
                return 'Update Location';
            }
            if (this.locationStatus === 'error') {
                return 'Retry Location';
            }
            return this.hasLocation ? 'Update Location' : 'Get My Location';
        },
        hasLocation() {
            return this.locationData !== null;
        },
        locationTimeText() {
            if (!this.locationData || !this.locationData.timestamp) {
                return '';
            }
            return getRelativeTime(this.locationData.timestamp);
        }
    },
    mounted() {
        this.loadSavedLocation();
    },
    methods: {
        loadSavedLocation() {
            try {
                this.locationData = getUserLocation();
                if (this.locationData) {
                    this.locationStatus = 'success';
                }
            } catch (error) {
                console.error('Error loading saved location:', error);
                this.clearLocation();
            }
        },

        async updateLocation() {
            if (!navigator.geolocation) {
                this.showError('Geolocation is not supported by this browser.');
                return;
            }

            this.isLocating = true;
            this.locationStatus = 'locating';
            this.errorMessage = '';

            try {
                const position = await this.getCurrentPosition();
                const locationData = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy
                };

                // Save to localStorage using utility
                setUserLocation(locationData);
                this.locationData = getUserLocation();
                this.locationStatus = 'success';

                // Emit event to parent components
                this.$emit('location-updated', this.locationData);

                // Clear success status after 3 seconds
                setTimeout(() => {
                    if (this.locationStatus === 'success') {
                        this.locationStatus = null;
                    }
                }, 3000);

            } catch (error) {
                this.handleGeolocationError(error);
            } finally {
                this.isLocating = false;
            }
        },

        getCurrentPosition() {
            return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    resolve,
                    reject,
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 60000 // 1 minute
                    }
                );
            });
        },

        handleGeolocationError(error) {
            let message = 'Failed to get your location. ';
            
            switch (error.code) {
                case error.PERMISSION_DENIED:
                    message += 'Please enable location permissions in your browser settings and try again.';
                    break;
                case error.POSITION_UNAVAILABLE:
                    message += 'Location information is unavailable. Please try again later.';
                    break;
                case error.TIMEOUT:
                    message += 'Location request timed out. Please try again.';
                    break;
                default:
                    message += 'An unknown error occurred. Please try again.';
                    break;
            }
            
            this.showError(message);
        },

        showError(message) {
            this.errorMessage = message;
            this.locationStatus = 'error';
            
            // Clear error status after 5 seconds
            setTimeout(() => {
                if (this.locationStatus === 'error') {
                    this.locationStatus = null;
                    this.errorMessage = '';
                }
            }, 5000);
        },

        clearLocation() {
            try {
                clearUserLocation();
                this.locationData = null;
                this.locationStatus = null;
                this.errorMessage = '';
                
                // Emit event to parent components
                this.$emit('location-cleared');
                
            } catch (error) {
                console.error('Error clearing location:', error);
            }
        },

        // Public method for parent components to get current location data
        getLocationData() {
            return this.locationData;
        }
    }
}
</script>

<style scoped>
.update-location-component {
    margin-bottom: 1rem;
}

.location-controls {
    flex-wrap: wrap;
}

.alert-sm {
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
}

@media (max-width: 576px) {
    .location-controls {
        flex-direction: column;
        align-items: stretch;
    }
    
    .location-controls .btn {
        margin-bottom: 0.5rem;
    }
    
    .location-controls .text-muted {
        text-align: center;
    }
}
</style>